import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TenantsAPI } from "@/lib/sprint2-api";
import { ParentsAPI, StudentsAPI } from "@/lib/sprint3-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Users, Loader2, Trash2, LinkIcon, Search, Copy, Check } from "lucide-react";

const RELATIONS = ["father", "mother", "guardian", "other"];

export default function Parents() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState(isSuper ? "" : user?.tenant_id || "");
  const [search, setSearch] = useState("");
  const [linkParent, setLinkParent] = useState(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  useEffect(() => {
    if (isSuper && !tenantId && tenantsQuery.data?.length) setTenantId(tenantsQuery.data[0].id);
  }, [isSuper, tenantId, tenantsQuery.data]);
  const tenantParam = isSuper && tenantId ? { tenant_id: tenantId } : undefined;
  const effectiveTenant = isSuper ? tenantId : user?.tenant_id;

  const parentsQuery = useQuery({
    queryKey: ["parents", effectiveTenant, search],
    queryFn: () => ParentsAPI.list({ ...(tenantParam || {}), ...(search ? { q: search } : {}) }),
    enabled: !!effectiveTenant,
  });

  return (
    <AppShell>
      <div className="p-8" data-testid="parents-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Parents</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Parent accounts are created with login credentials. Link them to
              their children to give them access to attendance, fees and notices.
            </p>
          </div>
          <CreateParent
            tenantId={effectiveTenant}
            isSuper={isSuper}
            onDone={() => queryClient.invalidateQueries({ queryKey: ["parents"] })}
          />
        </div>

        <div className="flex flex-wrap gap-3 mb-5 p-4 border border-border rounded-md bg-card">
          {isSuper && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="parents-scope-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 flex-1 min-w-[240px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Name or email…" data-testid="parents-search" />
            </div>
          </div>
        </div>

        <div className="border border-border rounded-md bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Linked students</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parentsQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : !parentsQuery.data?.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12">
                  <Users className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No parents yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Add parents to give families self-service access.</p>
                </TableCell></TableRow>
              ) : parentsQuery.data.map((p) => (
                <TableRow key={p.id} data-testid={`parent-row-${p.email}`}>
                  <TableCell><div className="font-medium">{p.first_name} {p.last_name}</div></TableCell>
                  <TableCell className="font-mono text-xs">{p.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.phone || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">{p.student_count}</Badge>
                  </TableCell>
                  <TableCell>
                    {p.is_active
                      ? <Badge className="bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2]">Active</Badge>
                      : <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setLinkParent(p)} data-testid={`parent-link-${p.email}`}>
                      <LinkIcon className="h-3.5 w-3.5 mr-1" /> Children
                    </Button>
                    <Button size="icon" variant="ghost" onClick={async () => {
                      if (!window.confirm("Deactivate this parent?")) return;
                      try { await ParentsAPI.remove(p.id); toast.success("Parent deactivated"); queryClient.invalidateQueries({ queryKey: ["parents"] }); }
                      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                    }} data-testid={`parent-delete-${p.email}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {linkParent && (
          <LinkChildrenDialog
            parent={linkParent}
            tenantParam={tenantParam}
            onClose={() => { setLinkParent(null); queryClient.invalidateQueries({ queryKey: ["parents"] }); }}
          />
        )}
      </div>
    </AppShell>
  );
}

function CreateParent({ tenantId, isSuper, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", occupation: "", password: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const out = await ParentsAPI.create({
        ...form,
        tenant_id: isSuper ? tenantId : undefined,
        password: form.password || undefined,
      });
      setResult(out);
      toast.success(`Parent ${out.parent.first_name} created`);
      onDone?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const reset = () => { setForm({ first_name: "", last_name: "", email: "", phone: "", occupation: "", password: "" }); setResult(null); setError(""); };
  const copy = async (text) => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button disabled={!tenantId} data-testid="create-parent-button"><Plus className="h-4 w-4 mr-1.5" />Parent</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{result ? "Parent created" : "New parent"}</DialogTitle>
          <DialogDescription>{result ? "Share the credentials below." : "Creates a login + parent profile."}</DialogDescription>
        </DialogHeader>
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="First name"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="parent-form-first-name" /></F>
              <F label="Last name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="parent-form-last-name" /></F>
            </div>
            <F label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="parent-form-email" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="parent-form-phone" /></F>
              <F label="Occupation"><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></F>
            </div>
            <F label="Password (optional — auto-generated)"><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="auto" className="font-mono" data-testid="parent-form-password" /></F>
            {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="parent-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create</Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="parent-create-success">
            <div className="text-sm">Login email: <code className="font-mono">{result.parent.email}</code></div>
            {result.temporary_password && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Temporary password</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-xs bg-secondary px-2 py-1.5 rounded border" data-testid="parent-temp-password">{result.temporary_password}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(result.temporary_password)}>{copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}</Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={reset}>Add another</Button>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkChildrenDialog({ parent, tenantParam, onClose }) {
  const queryClient = useQueryClient();
  const linksQuery = useQuery({
    queryKey: ["links", parent.id],
    queryFn: () => ParentsAPI.listLinks(parent.id),
  });
  const studentsQuery = useQuery({
    queryKey: ["students-pickable", parent.tenant_id, tenantParam],
    queryFn: () => StudentsAPI.list({ ...(tenantParam || {}), limit: 500 }),
  });
  const [selectedStudent, setSelectedStudent] = useState("");
  const [relation, setRelation] = useState("father");
  const [error, setError] = useState("");

  const linkedIds = new Set((linksQuery.data || []).map((l) => l.student_id));
  const linkedRows = (linksQuery.data || []).map((l) => {
    const stu = studentsQuery.data?.find((s) => s.id === l.student_id);
    return { ...l, student: stu };
  });
  const available = (studentsQuery.data || []).filter((s) => !linkedIds.has(s.id));

  const add = async () => {
    if (!selectedStudent) return;
    setError("");
    try {
      await ParentsAPI.addLink(parent.id, { student_id: selectedStudent, relation });
      toast.success("Linked");
      setSelectedStudent("");
      queryClient.invalidateQueries({ queryKey: ["links", parent.id] });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeLink = async (id) => {
    try { await ParentsAPI.removeLink(parent.id, id); toast.success("Unlinked"); queryClient.invalidateQueries({ queryKey: ["links", parent.id] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="link-children-dialog">
        <DialogHeader>
          <DialogTitle>{parent.first_name} {parent.last_name} — Linked children</DialogTitle>
          <DialogDescription>{parent.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border border-border rounded-md p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Link a student</div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-3">
              <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                <SelectTrigger data-testid="link-student-select"><SelectValue placeholder={`Select student (${available.length})`} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {available.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} — {s.admission_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={relation} onValueChange={setRelation}>
                <SelectTrigger className="w-[120px]" data-testid="link-relation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={add} disabled={!selectedStudent} data-testid="link-add-button"><LinkIcon className="h-3.5 w-3.5 mr-1.5" />Link</Button>
            </div>
            {error && <div className="text-xs text-destructive">{error}</div>}
          </div>

          <div className="border border-border rounded-md">
            <Table>
              <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Relation</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {linkedRows.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-8 text-sm text-muted-foreground">No children linked yet.</TableCell></TableRow>
                ) : linkedRows.map((l) => (
                  <TableRow key={l.id} data-testid={`linked-student-${l.student_id}`}>
                    <TableCell>
                      {l.student ? (
                        <>
                          <div className="font-medium">{l.student.first_name} {l.student.last_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{l.student.admission_no}</div>
                        </>
                      ) : <span className="text-xs text-muted-foreground font-mono">{l.student_id}</span>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{l.relation}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => removeLink(l.id)} data-testid={`unlink-${l.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="link-close-button">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
